import React, { useState, useEffect } from 'react';
import { useAuth, useLanguage } from '../context/AppContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Shield, UserPlus, Trash2, Edit, Building2, Lock,
  ChevronDown, ChevronRight, Mail, Users, Check
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const CoAdminsPage = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [coAdmins, setCoAdmins] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [permCatalog, setPermCatalog] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editAdmin, setEditAdmin] = useState(null);

  const fr = language === 'fr';

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [caRes, statsRes, permRes] = await Promise.all([
        axios.get(`${API_URL}/api/co-admins`),
        axios.get(`${API_URL}/api/admin/stats/global`),
        axios.get(`${API_URL}/api/permissions/catalog`),
      ]);
      setCoAdmins(caRes.data);
      setUniversities(statsRes.data?.universities || []);
      setPermCatalog(permRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleDeactivate = async (userId) => {
    try {
      await axios.delete(`${API_URL}/api/co-admins/${userId}`);
      toast.success(fr ? 'Co-admin desactive' : 'Co-admin deactivated');
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  if (loading) return <div className="animate-pulse space-y-4">{[1,2,3].map(i => <div key={i} className="h-20 bg-slate-100 rounded-lg" />)}</div>;

  return (
    <div className="space-y-6" data-testid="co-admins-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="co-admins-title">
            {fr ? 'Co-Administrateurs' : 'Co-Administrators'}
          </h1>
          <p className="text-sm text-slate-500">
            {fr ? 'Gerez les co-administrateurs et leurs acces aux universites' : 'Manage co-administrators and their university access'}
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="gap-2" data-testid="add-co-admin-btn">
          <UserPlus className="w-4 h-4" /> {fr ? 'Ajouter' : 'Add'}
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Shield className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{coAdmins.length}</p>
              <p className="text-xs text-slate-500">{fr ? 'Co-admins' : 'Co-admins'}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <Check className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{coAdmins.filter(c => c.is_active).length}</p>
              <p className="text-xs text-slate-500">{fr ? 'Actifs' : 'Active'}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{universities.length}</p>
              <p className="text-xs text-slate-500">{fr ? 'Universites' : 'Universities'}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Co-Admin List */}
      {coAdmins.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Shield className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-500">{fr ? 'Aucun co-administrateur nomme' : 'No co-admins assigned'}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {coAdmins.map(admin => (
            <Card key={admin.id} data-testid={`co-admin-card-${admin.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-11 h-11 rounded-full bg-[#0F2C59] text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                      {(admin.first_name?.[0] || '').toUpperCase()}{(admin.last_name?.[0] || '').toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm">{admin.first_name} {admin.last_name}</span>
                        <Badge className={admin.is_active ? 'bg-green-100 text-green-700 text-[10px]' : 'bg-red-100 text-red-700 text-[10px]'}>
                          {admin.is_active ? (fr ? 'Actif' : 'Active') : (fr ? 'Inactif' : 'Inactive')}
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-500 flex items-center gap-1">
                        <Mail className="w-3 h-3" />{admin.email}
                      </p>

                      {/* Assigned Universities */}
                      {admin.assigned_university_details?.length > 0 && (
                        <div className="flex gap-1.5 mt-2 flex-wrap">
                          {admin.assigned_university_details.map(uni => (
                            <Badge key={uni.id} className="bg-blue-50 text-blue-700 text-[10px] gap-1">
                              <Building2 className="w-3 h-3" />{uni.name}
                            </Badge>
                          ))}
                        </div>
                      )}
                      {(!admin.assigned_university_details || admin.assigned_university_details.length === 0) && (
                        <p className="text-[10px] text-amber-600 mt-1">
                          {fr ? 'Aucune universite assignee' : 'No university assigned'}
                        </p>
                      )}

                      {/* Permissions */}
                      {admin.permissions?.length > 0 && (
                        <div className="flex gap-1 mt-1.5 flex-wrap">
                          {admin.permissions.slice(0, 6).map(p => (
                            <Badge key={p} variant="outline" className="text-[9px] py-0 font-mono">{p}</Badge>
                          ))}
                          {admin.permissions.length > 6 && (
                            <Badge variant="outline" className="text-[9px] py-0">+{admin.permissions.length - 6}</Badge>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setEditAdmin(admin)} data-testid={`edit-co-admin-${admin.id}`}>
                      <Edit className="w-4 h-4 text-slate-500" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700" onClick={() => handleDeactivate(admin.id)} data-testid={`deactivate-co-admin-${admin.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {(showCreate || editAdmin) && (
        <CoAdminDialog
          admin={editAdmin}
          universities={universities}
          permCatalog={permCatalog}
          language={language}
          onClose={() => { setShowCreate(false); setEditAdmin(null); loadData(); }}
        />
      )}
    </div>
  );
};

const CoAdminDialog = ({ admin, universities, permCatalog, language, onClose }) => {
  const fr = language === 'fr';
  const isEdit = !!admin;
  const [form, setForm] = useState({
    email: admin?.email || '',
    password: '',
    first_name: admin?.first_name || '',
    last_name: admin?.last_name || '',
    phone: admin?.phone || '',
  });
  const [selectedUnis, setSelectedUnis] = useState(admin?.assigned_universities || []);
  const [selectedPerms, setSelectedPerms] = useState(admin?.permissions || []);
  const [loading, setLoading] = useState(false);
  const [expandedCats, setExpandedCats] = useState({});

  const toggleCat = (cat) => setExpandedCats(p => ({ ...p, [cat]: !p[cat] }));

  const togglePerm = (perm) => {
    setSelectedPerms(p => p.includes(perm) ? p.filter(x => x !== perm) : [...p, perm]);
  };

  const toggleUni = (uniId) => {
    setSelectedUnis(p => p.includes(uniId) ? p.filter(x => x !== uniId) : [...p, uniId]);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (isEdit) {
        await axios.put(`${API_URL}/api/co-admins/${admin.id}`, {
          assigned_universities: selectedUnis,
          permissions: selectedPerms,
        });
        toast.success(fr ? 'Co-admin mis a jour' : 'Co-admin updated');
      } else {
        if (!form.email || !form.password || !form.first_name) {
          toast.error(fr ? 'Champs requis manquants' : 'Missing required fields');
          setLoading(false);
          return;
        }
        await axios.post(`${API_URL}/api/co-admins`, {
          ...form,
          assigned_universities: selectedUnis,
          permissions: selectedPerms,
        });
        toast.success(fr ? 'Co-administrateur cree' : 'Co-admin created');
      }
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{isEdit ? (fr ? 'Modifier le co-admin' : 'Edit co-admin') : (fr ? 'Nouveau co-administrateur' : 'New co-administrator')}</DialogTitle>
        </DialogHeader>

        <ScrollArea className="flex-1 pr-2">
          <div className="space-y-4">
            {!isEdit && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>{fr ? 'Prenom' : 'First name'}</Label>
                    <Input value={form.first_name} onChange={e => setForm(p => ({...p, first_name: e.target.value}))} data-testid="ca-first-name" />
                  </div>
                  <div>
                    <Label>{fr ? 'Nom' : 'Last name'}</Label>
                    <Input value={form.last_name} onChange={e => setForm(p => ({...p, last_name: e.target.value}))} data-testid="ca-last-name" />
                  </div>
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} data-testid="ca-email" />
                </div>
                <div>
                  <Label>{fr ? 'Mot de passe' : 'Password'}</Label>
                  <Input type="password" value={form.password} onChange={e => setForm(p => ({...p, password: e.target.value}))} data-testid="ca-password" />
                </div>
              </>
            )}

            {/* University Assignment */}
            <div>
              <Label className="mb-2 block flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                {fr ? 'Universites assignees' : 'Assigned universities'} ({selectedUnis.length})
              </Label>
              <div className="border rounded-lg p-2 space-y-1 max-h-[180px] overflow-y-auto">
                {universities.length === 0 ? (
                  <p className="text-xs text-slate-400 p-2 text-center">{fr ? 'Aucune universite disponible' : 'No universities available'}</p>
                ) : (
                  universities.map(uni => (
                    <label key={uni.id} className="flex items-center gap-2 p-2 rounded hover:bg-slate-50 cursor-pointer">
                      <Checkbox
                        checked={selectedUnis.includes(uni.id)}
                        onCheckedChange={() => toggleUni(uni.id)}
                        data-testid={`uni-check-${uni.code}`}
                      />
                      <div className="flex items-center gap-2 flex-1">
                        <Building2 className="w-4 h-4 text-blue-500" />
                        <span className="text-sm">{uni.name}</span>
                        <Badge variant="outline" className="text-[10px] font-mono ml-auto">{uni.code}</Badge>
                      </div>
                    </label>
                  ))
                )}
              </div>
            </div>

            {/* Permissions */}
            <div>
              <Label className="mb-2 block flex items-center gap-2">
                <Lock className="w-4 h-4" />
                {fr ? 'Permissions' : 'Permissions'} ({selectedPerms.length})
              </Label>
              <div className="border rounded-lg p-2 max-h-[250px] overflow-y-auto">
                {Object.entries(permCatalog).map(([catKey, cat]) => {
                  const catPerms = cat.permissions.map(([k]) => k);
                  const selectedCount = catPerms.filter(p => selectedPerms.includes(p)).length;
                  const expanded = expandedCats[catKey];
                  return (
                    <div key={catKey} className="mb-1">
                      <button
                        onClick={() => toggleCat(catKey)}
                        className="w-full flex items-center justify-between p-2 rounded-md hover:bg-slate-50 text-sm"
                      >
                        <div className="flex items-center gap-2">
                          {expanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                          <span className="font-medium text-slate-700">{cat.label_fr}</span>
                        </div>
                        <Badge variant="outline" className="text-[10px]">{selectedCount}/{catPerms.length}</Badge>
                      </button>
                      {expanded && (
                        <div className="ml-6 space-y-1 pb-2">
                          {cat.permissions.map(([permKey, permLabel]) => (
                            <label key={permKey} className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-50 cursor-pointer text-sm">
                              <Checkbox
                                checked={selectedPerms.includes(permKey)}
                                onCheckedChange={() => togglePerm(permKey)}
                              />
                              <span className="text-slate-600">{permLabel}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </ScrollArea>

        <div className="flex justify-end gap-2 pt-3 border-t">
          <Button variant="outline" onClick={onClose}>{fr ? 'Annuler' : 'Cancel'}</Button>
          <Button onClick={handleSubmit} disabled={loading} data-testid="submit-co-admin-btn">
            {loading ? '...' : isEdit ? (fr ? 'Sauvegarder' : 'Save') : (fr ? 'Creer' : 'Create')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CoAdminsPage;
